import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import validator from "validator";
import {
  signInWithEmailAndPassword,
  setPersistence,
  browserSessionPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { auth } from "../../../../../lib/config/firebaseInit";
import { toast } from "../../../../../utilities/toast";
import {
  resetPasswordLink,
  resetPasswordMail,
} from "../../../../../lib/webService/webService";
import { Eye, EyeSlash } from "react-bootstrap-icons";
import { consumeReturnPath } from "../../../../../utilities/authRedirect";

if (typeof document !== "undefined" && !document.getElementById("ol-styles")) {
  const s = document.createElement("style");
  s.id = "ol-styles";
  s.textContent = `
    .ol-wrap{min-height:100vh;display:flex;align-items:center;justify-content:center;background:#F2F2F7;padding:24px 16px;font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display","Helvetica Neue",Arial,sans-serif}
    .ol-card{background:#fff;border-radius:20px;padding:36px 28px 32px;width:100%;max-width:400px;box-shadow:0 2px 16px rgba(0,0,0,.07)}
    .ol-input{width:100%;background:rgba(118,118,128,.10);border:1.5px solid transparent;border-radius:10px;padding:11px 14px;font-size:.9375rem;color:#1c1c1e;outline:none;transition:border-color .18s,box-shadow .18s;box-sizing:border-box;font-family:inherit}
    .ol-input:focus{border-color:#007AFF;box-shadow:0 0 0 3px rgba(0,122,255,.12)}
    .ol-input::placeholder{color:rgba(60,60,67,.38)}
    .ol-btn{width:100%;padding:13px;border-radius:12px;border:none;font-size:.9375rem;font-weight:600;cursor:pointer;font-family:inherit;transition:opacity .15s;display:flex;align-items:center;justify-content:center;gap:8px}
    .ol-btn:disabled{opacity:.55;cursor:not-allowed}
    .ol-btn-primary{background:#007AFF;color:#fff}
    .ol-btn-primary:active{opacity:.82}
    .ol-label{font-size:.8125rem;font-weight:500;color:rgba(60,60,67,.65);margin-bottom:6px;display:block}
    .ol-pw-wrap{position:relative}
    .ol-pw-eye{position:absolute;right:12px;top:50%;transform:translateY(-50%);background:none;border:none;padding:0;cursor:pointer;color:rgba(60,60,67,.45);display:flex;align-items:center}
    .ol-err{background:rgba(255,59,48,.08);border:1px solid rgba(255,59,48,.18);border-radius:10px;padding:10px 14px;color:#FF3B30;font-size:.875rem;margin-bottom:16px;display:flex;justify-content:space-between;align-items:flex-start;gap:8px}
    @keyframes _ol_spin{to{transform:rotate(360deg)}}
    ._ol_spin{width:16px;height:16px;border:2px solid rgba(255,255,255,.35);border-top-color:#fff;border-radius:50%;animation:_ol_spin .7s linear infinite;flex-shrink:0}
  `;
  document.head.appendChild(s);
}

const OperationsLogin = () => {
  const [inputEmail, setInputEmail]       = useState("");
  const [inputPassword, setInputPassword] = useState("");
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState({ isErr: false, message: "" });
  const [showPassword, setShowPassword]   = useState(false);
  const [switchState, setSwitchState]     = useState(false);
  const [btnResetLoading, setBtnResetLoading] = useState(false);
  const navigate = useNavigate();

  async function login(email, password) {
    try {
      const persistence = switchState ? browserLocalPersistence : browserSessionPersistence;
      await setPersistence(auth, persistence);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      if (user) {
        await user.getIdToken(true);
        toast.success("Welcome back! Logging you in...");
        navigate(consumeReturnPath() ?? "/ops", { replace: true });
      }
    } catch (err) {
      const msgs = {
        'auth/invalid-email': 'Invalid email address format',
        'auth/user-disabled': 'This account has been disabled. Please contact support',
        'auth/user-not-found': 'No account found with this email',
        'auth/wrong-password': 'Incorrect password',
        'auth/invalid-credential': 'Invalid email or password',
        'auth/too-many-requests': 'Too many failed attempts. Please try again later',
        'auth/network-request-failed': 'Network error. Please check your connection',
        'auth/operation-not-allowed': 'Email/password accounts are not enabled',
        'auth/requires-recent-login': 'Please log in again to continue',
      };
      toast.error(msgs[err.code] || 'An error occurred during login. Please try again');
      setLoading(false);
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!inputEmail || !inputPassword) {
      setError({ isErr: true, message: "All fields are required" });
      setLoading(false); return;
    }
    if (!validator.isEmail(inputEmail)) {
      setError({ isErr: true, message: "Invalid email format" });
      setLoading(false); return;
    }
    if (inputPassword.length < 6) {
      setError({ isErr: true, message: "Minimum password length is 6" });
      setLoading(false); return;
    }
    if (inputPassword.length > 16) {
      setError({ isErr: true, message: "Maximum password length is 16" });
      setLoading(false); return;
    }
    await login(inputEmail, inputPassword);
  };

  const resetPassword = async () => {
    if (!inputEmail) { toast.error("Email is required"); return; }
    if (!validator.isEmail(inputEmail)) { toast.error("Invalid email format"); return; }
    try {
      setBtnResetLoading(true);
      const link = await resetPasswordLink(inputEmail);
      await resetPasswordMail(inputEmail, link);
      toast.info(`Password reset instructions sent to ${inputEmail}`);
    } catch (err) {
      const msgs = {
        'auth/invalid-email': 'Invalid email address format',
        'auth/user-not-found': 'No account found with this email',
        'auth/too-many-requests': 'Too many attempts. Please try again later',
        'auth/network-request-failed': 'Network error. Please check your connection',
      };
      toast.error(msgs[err.code] || 'Failed to send reset email. Please try again');
    } finally {
      setBtnResetLoading(false);
    }
  };

  return (
    <div className="ol-wrap">
      <div className="ol-card">

        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <img
            src="/imgs/logo/complete-250.png"
            alt="Logo"
            style={{ width: 80, height: 80, objectFit: "contain", marginBottom: 16 }}
          />
          <div style={{ fontSize: "1.375rem", fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.02em" }}>
            Rider Login
          </div>
          <div style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.55)", marginTop: 4 }}>
            Welcome back
          </div>
        </div>

        {error.isErr && (
          <div className="ol-err">
            <span>{error.message}</span>
            <button
              onClick={() => setError({ isErr: false, message: "" })}
              style={{ background: "none", border: "none", color: "#FF3B30", cursor: "pointer", padding: 0, lineHeight: 1, flexShrink: 0 }}
            >✕</button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <label className="ol-label">Email</label>
            <input
              className="ol-input"
              type="email"
              placeholder="Enter email"
              value={inputEmail}
              onChange={(e) => setInputEmail(e.target.value)}
              required
            />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label className="ol-label">Password</label>
            <div className="ol-pw-wrap">
              <input
                className="ol-input"
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                style={{ paddingRight: 40 }}
                value={inputPassword}
                onChange={(e) => setInputPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
              <button type="button" className="ol-pw-eye" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeSlash size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 22 }}>
            <input
              type="checkbox"
              id="ol-remember"
              checked={switchState}
              onChange={() => setSwitchState(!switchState)}
              style={{ width: 16, height: 16, accentColor: "#007AFF", cursor: "pointer" }}
            />
            <label htmlFor="ol-remember" style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.7)", margin: 0, cursor: "pointer" }}>
              Remember me
            </label>
          </div>

          <button type="submit" className="ol-btn ol-btn-primary" disabled={loading}>
            {loading ? <><span className="_ol_spin" />Signing in...</> : "Sign In"}
          </button>
        </form>

        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button
            type="button"
            onClick={resetPassword}
            disabled={btnResetLoading}
            style={{ background: "none", border: "none", color: "#007AFF", fontSize: "0.875rem", cursor: "pointer", fontFamily: "inherit", opacity: btnResetLoading ? 0.6 : 1 }}
          >
            {btnResetLoading ? "Sending reset email..." : "Forgot password?"}
          </button>
        </div>

        <div style={{ textAlign: "center", marginTop: 20, fontSize: "0.875rem", color: "rgba(60,60,67,0.55)" }}>
          Don't have an account?{" "}
          <Link to="/ops/register" style={{ color: "#007AFF", textDecoration: "none", fontWeight: 500 }}>
            Register
          </Link>
        </div>

      </div>
    </div>
  );
};

export default OperationsLogin;
